import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { ChevronRight, Mail, MessageSquare, Send, Swords } from 'lucide-react';

import { useQlashUser } from '@/hooks/useQlashUser';
import { useLimuloIsTheSource } from '@/hooks/useLimuloIsTheSource';
import { useGetMyLinkQuery } from '@/services/qduelsApi';
import { isNotLinkedError } from '@/services/qduelsErrors';
import { useGetLimuloGamesQuery, useGetLimuloProfilesQuery } from '@/services/limuloApi';
import {
  limuloClashRoyaleGame,
  limuloProfileForGame,
  limuloProfileOwnershipVerified,
} from '@/lib/limulo/profiles';
import { LIMULO_PROFILES_URL, limuloProfileCreateUrl } from '@/lib/limulo/routes';
import { useMeQuery as useQbotMeQuery } from '@/services/qbotApi';
import { useLinkedIdentities } from '@/hooks/useLinkedIdentities';
import { canLinkSocial, canUnlinkSocial, startSocialLink, unlinkSocial } from '@/lib/accountLink';
import { cn } from '@/lib/cn';
import { OSCURA } from '@/lib/v3';

/**
 * Social identities shown in the "Personal accounts" group. Logto-managed
 * (discord/google/apple/twitch) come from userinfo; Telegram from initData +
 * QBot linkedAccounts. Google/Apple show the associated email (preferEmail).
 */
const SOCIAL_PROVIDERS: ReadonlyArray<{
  target: string;
  label: string;
  className: string;
  icon?: React.ReactNode;
  monogram?: string;
  preferEmail?: boolean;
}> = [
  { target: 'telegram', label: 'Telegram', className: 'bg-sky-500/20 text-sky-400', icon: <Send className="h-4 w-4" /> },
  { target: 'discord', label: 'Discord', className: 'bg-[#5865F2]/20 text-[#5865F2]', icon: <MessageSquare className="h-4 w-4" /> },
  { target: 'google', label: 'Google', className: 'bg-[#EA4335]/20 text-[#EA4335]', monogram: 'G', preferEmail: true },
  { target: 'apple', label: 'Apple', className: 'bg-fg/10 text-fg', monogram: 'A', preferEmail: true },
  { target: 'twitch', label: 'Twitch', className: 'bg-[#9146FF]/20 text-[#9146FF]', monogram: 'Tw' },
];

/**
 * Accounts page — reached from the Profilo "Settings" block. Two separate
 * groups on one page: linked GAME accounts (Clash Royale via qduels) and
 * PERSONAL accounts (the login email first, then Telegram/Discord/Google/
 * Apple/Twitch with live connection state).
 *
 * ═══ 🔴 LA RIGA TELEGRAM DICEVA «COLLEGATO» A CHIUNQUE (corretto qui) ═══
 * Fino al 2026-09-08 lo stato di quella riga veniva da `useTelegram().user`, cioe'
 * dalla presenza di un utente Telegram nella Mini App: dentro Telegram c'e' sempre,
 * quindi la riga diceva «Connected» **a tutti**, compreso chi non ha nessuna identita'
 * Telegram sull'account QLASH.
 * 🔑 Sono due fatti diversi: ***«sei dentro Telegram» e «il tuo account QLASH ha
 * un'identita' Telegram»*** — e solo il secondo e' qualcosa su cui l'utente puo' agire.
 * Il difetto era invisibile finche' non c'era niente da fare; con un bottone «Collega»
 * accanto diventa una contraddizione a schermo.
 * ⇒ Decisione di Luca (voce L-692, 2026-09-08T20:17:25Z): **«Collegato» solo a chi ha
 * l'identita' su Logto.** Ora la riga legge `identities.telegram` da `oidc/me`, come
 * gia' facevano gli altri quattro provider.
 *
 * ⚠️ Resta FUORI, ed e' una terza cosa ancora: il legame `LinkedAccount` di QBot
 * (ADR-0107), che governa **chi puo' scrivere in DM** e non l'identita' dell'account.
 * Non si fondono in una riga sola: dicono cose diverse e si rompono in modi diversi.
 *
 * Collegamento e scollegamento vivono in `lib/accountLink.ts`. Lo **scollegamento** e'
 * arrivato dopo, su richiesta di Luca (09/09, testuale: *«vorrei anche poter vedere il
 * bottone o funzione o modalita' per scollegare account da la' se gia' collegato»*):
 * era stato dichiarato fuori scopo perche' l'approvazione parlava del solo «Collega»,
 * ed e' rientrato quando l'ha chiesto lui — non prima.
 */
export function LinkedAccounts() {
  const { t } = useTranslation('general');
  // Una fonte sola per build: su una build Limulo Laravel non viene interrogato affatto.
  // Il perche' (e la corsa che c'era prima) in `hooks/useQlashUser.ts`.
  const me = useQlashUser();

  const identities = useLinkedIdentities();
  const qbotMe = useQbotMeQuery();
  const qbotLinked = new Map<string, string>(
    (qbotMe.data?.linkedAccounts ?? []).map((a) => [a.platform, a.platformUserId]),
  );

  // `null` = nessun giro aperto. Tiene i bottoni fermi mentre uno parte: due giri
  // insieme sovrascriverebbero lo stesso record in attesa, e il secondo callback
  // troverebbe uno `state` che non e' il suo.
  const [inCorso, setInCorso] = useState<string | null>(null);
  const [errore, setErrore] = useState(false);

  async function avvia(target: string) {
    if (inCorso) return;
    setInCorso(target);
    setErrore(false);
    const giro = await startSocialLink(target as 'telegram');
    if (!giro) {
      // Non si resta su «Apro…»: un bottone che non torna e' indistinguibile da
      // uno che sta lavorando, ed e' il modo in cui un guasto diventa un'attesa.
      setInCorso(null);
      setErrore(true);
      return;
    }
    // Redirect dentro questa WebView: al ritorno `/auth/link-callback` chiude il
    // giro con i parametri che Telegram rimanda. Il perche' non ci sia (ancora) il
    // ponte al browser di sistema sta in `routes/AuthLinkCallback.tsx`.
    window.location.href = giro.authorizationUri;
  }

  // Il target per cui il prossimo tocco vale «sì»: scollegare non chiede nessuna
  // verifica al server (basta essere l'utente), quindi **l'unico attrito e' quello
  // che ci mettiamo noi**. Due tocchi, non un dialogo: la superficie e' un telefono.
  const [conferma, setConferma] = useState<string | null>(null);

  async function scollega(target: string) {
    if (inCorso) return;
    if (conferma !== target) {
      setConferma(target);
      return;
    }
    setConferma(null);
    setInCorso(target);
    setErrore(false);
    const ok = await unlinkSocial(target);
    setInCorso(null);
    if (!ok) {
      setErrore(true);
      return;
    }
    // Si rilegge dal server invece di spegnere la riga a mano: lo stato di questa
    // pagina e' cio' che Logto dice, e una riga spenta localmente su una `DELETE`
    // che non ha fatto quello che credevamo sarebbe una bugia con l'aria di un esito.
    window.location.reload();
  }

  // ═════════════════════════════════════════════════════════════════════════════
  // LA SOSTITUZIONE PER SEZIONE — qduels esce, il guscio resta.
  //
  // Stessa forma di `routes/Tournaments.tsx:159`: un booleano, non un <RoleGate>, perche'
  // qui non si avvolge una schermata — si sceglie DA DOVE legge una riga che rimane dov'e'.
  //
  // ⚠️ In PRODUZIONE non cambia niente, ma NON perche' questo codice sparisca — **c'e'**.
  // Misurato sul bundle di `npm run build` il 10/09: la copy di questo ramo e' presente in
  // un chunk, e accanto `featureLimuloEnabled:!1`. `VITE_FEATURE_LIMULO_ENABLED` e' opt-in e
  // `.env.production` non lo dichiara ⇒ il flag compila a `false` e **il ramo non si esegue
  // mai**. La differenza conta: cercare la propria copy in un bundle e trovarla non prova
  // che sia raggiungibile, e non trovarla non prova che sia spenta — *l'unica misura che
  // discrimina e' il VALORE del flag*. (Un `grep` di questo tipo ha gia' fatto concludere il
  // contrario a chi cercava una rotta Limulo in produzione.)
  // E' voluto: ADR-0106 §1 dice che in produzione QDuels
  // resta la verita' **finche' non esiste un Limulo di produzione**, e il momento del
  // passaggio e' il cutover di Limulo in prod, con un GO che non e' stato dato.
  // ⇒ Questo codice esiste per essere ACCESO da un flag, non per accendersi da se'.
  // ═════════════════════════════════════════════════════════════════════════════
  // Il predicato e' condiviso con Decks e Battles (`lib/limulo/sorgente.ts`): tre schermate che
  // rispondono alla stessa domanda non possono avere tre copie della riga.
  const limuloIsTheSource = useLimuloIsTheSource();

  const crLinkQuery = useGetMyLinkQuery(undefined, { skip: limuloIsTheSource });
  const crNotLinked = isNotLinkedError(crLinkQuery.error);
  const crLinkedTag = crLinkQuery.data?.playerTag ?? null;

  // Due rotte, e NON collassano in un unico stato — stessa ragione dichiarata in
  // `routes/limulo/LimuloProfiles.tsx`: se cadono i giochi ma non i profili, «non ho il
  // profilo» sarebbe una bugia, perche' non so nemmeno se il gioco esiste per Limulo.
  const limuloProfiles = useGetLimuloProfilesQuery(undefined, { skip: !limuloIsTheSource });
  const limuloGames = useGetLimuloGamesQuery(undefined, { skip: !limuloIsTheSource });

  const crGame = limuloClashRoyaleGame(limuloGames.data);
  const crProfile = limuloProfileForGame(limuloProfiles.data, crGame);

  // ⛔ NESSUNO STATO «VERIFICATO», e l'assenza e' deliberata.
  // `LimuloProfile` porta `status: string` e `profileValidation: unknown` (`types/limulo.ts`):
  // NON ho mai visto i valori che quei due campi assumono. Costruirci sopra un predicato
  // sarebbe inventare un significato — e una riga che dicesse «verificato» sbagliando lo
  // direbbe **nel verso che rassicura**. ⇒ due stati onesti adesso, il terzo quando qualcuno
  // avra' letto i valori veri. (Aperto in `reports/2026-09-10-passo2-gruppo-a-disegno.md`.)
  //
  // ⚠️ E il `gameId` per costruire l'URL di creazione e' il CODICE, mai l'`_id`: tre rotte su
  // cinque rispondono 424 con un ObjectId (`lib/limulo/routes.ts`, PR #122/#123). Se il
  // codice manca, non si offre una strada che porterebbe a un 424.
  const crCreateHref = crGame && crGame.gameId ? limuloProfileCreateUrl(crGame.gameId) : null;
  const crSorgenteMuta =
    limuloIsTheSource &&
    (limuloProfiles.isError ||
      limuloGames.isError ||
      (!limuloProfiles.isLoading && limuloProfiles.data === undefined) ||
      (!limuloGames.isLoading && limuloGames.data === undefined));

  // ⛔ IL TERZO STATO, e il predicato NON è quello che sembra: v. il blocco su
  // `limuloProfileOwnershipVerified`. In breve: la busta manda `valid: true` MENTRE
  // `ownershipVerified: false` (misurato il 10/09 su `stg-tma`), quindi `valid` avrebbe
  // acceso «verificato» su ogni profilo non verificato.
  // ⚠️ E il ramo «verificato» è SCRITTO E MAI OSSERVATO: su staging la chiave Clash Royale
  // dell'organizzazione non è configurata, quindi `ownershipVerified` è sempre falso e
  // nessuno ha mai visto questa riga accesa. Non è provata: è prevista.
  const crVerificato = limuloProfileOwnershipVerified(crProfile);

  const crHandle =
    crProfile?.handle || crProfile?.username || crProfile?.displayName || crProfile?.externalPlayerId || '';

  const crHref = !limuloIsTheSource
    ? '/duels/link'
    : crProfile
      ? LIMULO_PROFILES_URL
      : (crCreateHref ?? LIMULO_PROFILES_URL);

  return (
    <div className="flex flex-col gap-6 px-4 pb-6 pt-5">
      {/* I-318, ADR-0190 §1: titolo di sezione fuori dalla vista, `sr-only`; la chiave resta in uso. */}
      <header className="sr-only">
        <h1>
          {t('social.accounts_title', { defaultValue: 'Accounts' })}
        </h1>
      </header>

      {/* Group 1 — Linked game accounts */}
      <section className="flex flex-col gap-2">
        <h2 className="text-sm font-semibold uppercase tracking-wide text-fg-muted">
          {t('social.linked_games', { defaultValue: 'Linked game accounts' })}
        </h2>
        <Link
          to={crHref}
          className="card-edge flex items-center gap-3 rounded-xl bg-bg-secondary p-3 text-left transition-opacity active:opacity-80"
        >
          <span className="flex h-9 w-9 items-center justify-center rounded-full bg-brand/15 text-brand">
            <Swords className="h-4 w-4" />
          </span>
          <span className="min-w-0 flex-1">
            <p className="text-sm font-semibold text-fg">
              {t('social.link_clash_royale', { defaultValue: 'Clash Royale' })}
            </p>
            <p className="truncate text-[11px] text-fg-muted">
              {limuloIsTheSource
                ? crSorgenteMuta
                  ? // Non «non collegato»: NON LO SO. Sono due fatti diversi, e dirli uguali
                    // manderebbe l'utente a ricollegare un account che ha gia'.
                    t('social.link_cr_unknown', {
                      defaultValue: "I couldn't read this right now",
                    })
                  : crProfile
                    ? crVerificato
                      ? t('social.link_cr_limulo_verified', {
                          defaultValue: 'Linked and verified · {{handle}}',
                          handle: crHandle,
                        })
                      : t('social.link_cr_limulo_linked', {
                          defaultValue: 'Linked, not verified · {{handle}}',
                          handle: crHandle,
                        })
                    : crCreateHref
                      ? t('social.link_cr_limulo_connect', {
                          defaultValue: 'Link your game account',
                        })
                      : t('social.link_cr_unknown', {
                          defaultValue: "I couldn't read this right now",
                        })
                : crLinkedTag
                  ? t('social.link_cr_linked', { defaultValue: 'Linked · {{tag}}', tag: crLinkedTag })
                  : crNotLinked || !crLinkQuery.isLoading
                    ? t('social.link_cr_connect', { defaultValue: 'Link to send duels' })
                    : t('social.coming_soon', { defaultValue: 'Coming soon' })}
            </p>
          </span>
          <ChevronRight className="h-4 w-4 text-fg-muted" />
        </Link>
      </section>

      {/* Group 2 — Personal accounts: login email first, then social identities */}
      <section className="flex flex-col gap-2">
        <h2 className="text-sm font-semibold uppercase tracking-wide text-fg-muted">
          {t('social.personal_accounts', { defaultValue: 'Personal accounts' })}
        </h2>

        {/* Login email — a non-social account, always shown first. */}
        {me?.email && (
          <div className="card-edge flex items-center gap-3 rounded-xl bg-bg-secondary p-3">
            <span className="flex h-9 w-9 items-center justify-center rounded-full bg-fg/10 text-fg">
              <Mail className="h-4 w-4" />
            </span>
            <span className="min-w-0 flex-1">
              <p className="text-sm font-semibold text-fg">{t('email', { defaultValue: 'Email' })}</p>
              <p className="truncate text-[11px] text-fg-muted" {...OSCURA}>{me.email}</p>
            </span>
          </div>
        )}

        {errore && (
          <p className="text-[11px] text-danger">
            {t('social.link_failed_short', {
              defaultValue: 'Non è stato possibile aprire il collegamento. Riprova.',
            })}
          </p>
        )}

        {SOCIAL_PROVIDERS.map((p) => {
          const isTelegram = p.target === 'telegram';
          const identity = identities?.[p.target];
          const qbotId = qbotLinked.get(p.target);
          // 🔴 SI ASPETTA CHE TUTTE LE FONTI ABBIANO PARLATO, e non e' pignoleria:
          // prima si guardava solo `identities`, e fra la risposta di Logto (prima) e
          // quella di QBot (dopo) la riga **affermava** «non collegato» e mostrava il
          // bottone. Luca l'ha visto: *«ho visto per un attimo il bottone collega, poi
          // si e' collegato da solo»*. Un vuoto affermato su una domanda ancora aperta
          // e' la stessa famiglia dei cinque stati dei pannelli della Hub.
          const loading = identities === null || qbotMe.isLoading;
          const username = p.preferEmail
            ? (identity?.email ?? identity?.username ?? null)
            : (identity?.username ?? identity?.email ?? null);
          // ═══ 🔴 IL RAMO QBot PER TELEGRAM E' TORNATO, E LA RAGIONE PER CUI E' TORNATO
          //     VA LETTA PRIMA DI QUELLA PER CUI ERA STATO TOLTO ═══
          // In **#218** questo ramo e' stato RIMOSSO con una motivazione scritta: diceva
          // «Connected» spacciando il **legame DM di QBot** (ADR-0107) per l'**identita'
          // Logto**. Quella motivazione era giusta **finche' l'identita' si poteva
          // collegare.**
          // ⇒ **Non si puo' piu'**: decisione di Luca **L-761** (2026-09-10 11:21:44Z),
          //   *«(c) si rinuncia al collegamento da dentro la TMA»* — perche' in BotFather il
          //   dominio fidato per un bot e' uno solo ed e' quello del login (misurato).
          // ⇒ Da allora **il legame di QBot e' l'UNICO significato che «Telegram collegato»
          //   possa avere per la TMA**, e nasconderlo lascerebbe la riga a dire «non
          //   collegato» a chi ha fatto `/connect` — cioe' **una bugia al posto di un'altra**.
          // 🔑 ***Il difetto di #218 non era la FONTE: era la PAROLA.*** Per questo il ramo
          //   torna **con un'etichetta diversa** (sotto: «collegato per le notifiche», mai
          //   «Connected»), e chi legge #218 fra tre mesi deve trovare QUI il motivo per cui
          //   e' tornato — o togliera' il ramo una seconda volta citando una ragione scaduta.
          const isLinked = isTelegram ? !!identity || !!qbotId : !!identity || !!qbotId;
          // I due bottoni esistono solo dove la build sa a quale connector parlare
          // (`canLinkSocial`): su una build di produzione e' falso, e la riserva
          // «Account Center in produzione = GO a parte» non dipende da chi ricorda.
          // I due bottoni hanno prerequisiti DIVERSI, e tenerli sotto la stessa
          // condizione ha nascosto lo Scollega su Discord e Google (difetto visto da
          // Luca): collegare deve sapere a quale connector parlare, scollegare no.
          const collegabile = !loading && !isLinked && canLinkSocial(p.target);
          const scollegabile = !loading && isLinked && !!identity && canUnlinkSocial();
          return (
            <div
              key={p.target}
              className="card-edge flex items-center gap-3 rounded-xl bg-bg-secondary p-3"
            >
              <span
                className={cn(
                  'flex h-9 w-9 items-center justify-center rounded-full text-xs font-bold',
                  p.className,
                )}
              >
                {p.icon ?? p.monogram}
              </span>
              <span className="min-w-0 flex-1">
                <p className="text-sm font-semibold text-fg">{p.label}</p>
                <p className="truncate text-[11px] text-fg-muted" {...OSCURA}>
                  {loading
                    ? '…'
                    : isLinked
                      ? isTelegram && !identity
                        ? // 🔴 Legame QBot senza identita' Logto: e' il canale delle DM, NON
                          // l'account. La parola deve nominare la PORTATA, o si ricrea il
                          // difetto di #218 con un sinonimo.
                          t('social.linked_notifications', {
                            defaultValue: 'Collegato per le notifiche',
                          })
                        : username
                          ? t('social.connected_as', { defaultValue: 'Connected · {{name}}', name: username })
                          : t('social.connected', { defaultValue: 'Connected' })
                      : t('social.not_connected', { defaultValue: 'Not connected' })}
                </p>
              </span>
              {isLinked && !(isTelegram && !identity) && (
                <span className="text-xs font-medium text-brand">
                  {t('social.connected', { defaultValue: 'Connected' })}
                </span>
              )}
              {collegabile && (
                <button
                  onClick={() => void avvia(p.target)}
                  disabled={inCorso !== null}
                  className="rounded-md bg-brand px-3 py-1.5 text-xs font-medium text-brand-fg disabled:opacity-60"
                >
                  {inCorso === p.target
                    ? t('social.link_opening', { defaultValue: 'Apro…' })
                    : t('social.link_connect', { defaultValue: 'Collega' })}
                </button>
              )}
              {scollegabile && (
                <button
                  onClick={() => void scollega(p.target)}
                  disabled={inCorso !== null}
                  className="rounded-md border border-border px-3 py-1.5 text-xs font-medium text-fg-muted disabled:opacity-60"
                >
                  {inCorso === p.target
                    ? t('social.unlink_doing', { defaultValue: 'Scollego…' })
                    : conferma === p.target
                      ? t('social.unlink_confirm', { defaultValue: 'Confermi?' })
                      : t('social.unlink', { defaultValue: 'Scollega' })}
                </button>
              )}
            </div>
          );
        })}
      </section>
    </div>
  );
}
